import React from 'react'
import { t } from '@lingui/macro'
import { Box, Typography, Chip, Divider, Paper, Grid, alpha } from '@mui/material'
import { FormSubmission } from '@/core/models/form.model'
import { getEthicsPatientName, getEthicsSubmissionStatusLabel, getFieldDisplayValue } from '../lib/formatEthicsSubmission'

type EthicsSubmissionDetailProps = {
  submission: FormSubmission
}

export function EthicsSubmissionDetail({ submission }: EthicsSubmissionDetailProps) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {t`Submitted on`} {new Date(submission.submittedAt || submission.createdAt).toLocaleDateString()}
        </Typography>
        <Chip label={getEthicsSubmissionStatusLabel(submission.status)} size="small" />
      </Box>

      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {t`Patient`}
      </Typography>
      <Typography gutterBottom>{getEthicsPatientName(submission)}</Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {t`Responses`}
      </Typography>
      <Grid container spacing={2}>
        {submission.responses.map((response) => {
          const field = submission.template?.fields?.find((templateField) => templateField.id === response.fieldId)

          return (
            <Grid item xs={12} key={response.fieldId}>
              <Typography variant="body2" color="text.secondary">
                {field?.label || response.fieldId}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {getFieldDisplayValue(response.value)}
              </Typography>
            </Grid>
          )
        })}
      </Grid>

      {submission.ethicsResponse && (
        <>
          <Divider sx={{ my: 2 }} />
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.05),
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.success.main, 0.2),
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" color="success.main" gutterBottom>
              {t`Committee Response`}
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{submission.ethicsResponse}</Typography>
            {submission.respondedBy && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t`Responded by`} {submission.respondedBy.userInfo?.surname} {submission.respondedBy.userInfo?.name}
                {submission.respondedAt && <> - {new Date(submission.respondedAt).toLocaleDateString()}</>}
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  )
}
